import { jwtDecode } from "jwt-decode";
import { defineStore } from "pinia";

import getProfile, { ProfileResponse } from "@/modules/authorisation/api/getProfile";
import { getAuthClient } from "@/modules/authorisation/authClient";
import dayjs from "@/shared/plugins/dayjs";

import { Permission, Role, Token } from "./types";

export const useAuthorisationStore = defineStore("authorisation", {
  persist: true,
  state: (): {
    user?: {
      email?: string;
      name?: string;
      permissions?: Permission[];
      roles?: Role[];
      featureFlags?: { [key: string]: boolean };
    };
    userPreferences?: Record<string, any>;
    redirectToAfterLoginUrl?: string;
    profile?: ProfileResponse;
    profileLastFetchedAt?: string;
    tokenExpiresAt?: number;
  } => ({
    user: undefined,
    tokenExpiresAt: undefined,
    userPreferences: { isDarkMode: null },
    redirectToAfterLoginUrl: undefined,
    profileLastFetchedAt: undefined,
    profile: undefined
  }),
  actions: {
    setUserPreference(key: string, value: any) {
      if (!this.userPreferences) {
        this.userPreferences = {};
      }
      this.userPreferences[key] = value;
    },
    async syncClaims() {
      const authClient = await getAuthClient();
      const token = await authClient.getAccessToken();
      if (!token) {
        return;
      }
      await this.onTokenUpdate(token);
    },
    async onTokenUpdate(accessToken: string) {
      const payload = jwtDecode<Token>(accessToken);
      const expiresAt = payload.exp ? payload.exp * 1000 : undefined;

      if (expiresAt && expiresAt === this.tokenExpiresAt && this.user) {
        return;
      }

      const authClient = await getAuthClient();
      const kindeUser = await authClient.getUserProfile();

      this.user = {
        email: kindeUser?.email || undefined,
        name: kindeUser?.given_name || undefined,
        permissions: payload.permissions,
        roles: payload.roles,
        featureFlags: Object.entries(payload?.feature_flags || {})
          .map(([key, value]) => ({ [key]: value.v }))
          .reduce(
            (acc, cur) => ({
              ...acc,
              ...cur
            }),
            {}
          )
      };

      this.tokenExpiresAt = expiresAt;
    },
    async fetchProfileInformation(force: boolean = false) {
      if (this.profileLastFetchedAt && dayjs().diff(this.profileLastFetchedAt, "minutes") < 5 && !force) {
        return;
      }
      try {
        this.profileLastFetchedAt = dayjs().toISOString();
        const response = await getProfile();
        this.profile = response.data;
      } catch {
        this.profileLastFetchedAt = undefined;
      }
    }
  },
  getters: {
    hasFeature(state) {
      return (flag: string) => state.user?.featureFlags?.[flag] ?? false;
    },
    hasRole(state) {
      return (role: string) => state.user?.roles?.some(({ key }) => key === role) ?? false;
    },
    getRedirectToAfterLoginUrl(state) {
      if (
        state.redirectToAfterLoginUrl &&
        !["/auth/login", "/404", "/403"].some((path) => state.redirectToAfterLoginUrl!.includes(path))
      ) {
        return state.redirectToAfterLoginUrl;
      }
    }
  }
});
